import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import CheckAnimado from '@/components/movimento/CheckAnimado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import { chaveCarteiraLocal, formatarCentavos } from '@/pages/loja/utils';
import { NAVY, OURO, BORDA_CLARA, CARTAO } from '@/pages/loja/identidade';
import { fecharFaixa, faixaFechada, marcarConviteNaTela } from '@/lib/faixaApp';
import {
  estadoPermissao,
  ligarAvisosGerais,
  suportaAvisos,
  precisaInstalarNoIphone,
} from '@/lib/avisos';
import type { CarteiraPublica, ItemCarteira, PontoCarteira } from '@/pages/loja/types';
import CartaoVale, { TEMPO_VALE_MS } from './components/CartaoVale';
import ModalRetirada from './components/ModalRetirada';
import CartaoIngresso from './components/CartaoIngresso';
import ItemFicha from './components/ItemFicha';
import GuardarCarteira from './components/GuardarCarteira';
import ComoFunciona from './components/ComoFunciona';

const INTERVALO_POLLING_MS = 5000;

type Estado = 'carregando' | 'pronto' | 'nao_encontrado' | 'erro';

// A carteira devolve só o nome do produto; o desenho vem do catálogo do evento.
interface ProdutoVisual {
  imagem_url: string | null;
  imagem_slug: string | null;
  emoji: string | null;
}

interface GrupoItens {
  ponto: PontoCarteira;
  itens: ItemCarteira[];
}

const CARTAO_ESCURO = 'rounded-[16px] border';
const ESTILO_CARTAO = { borderColor: BORDA_CLARA, background: CARTAO };

// Ordem da tela (celular, na fila da barraca):
// cabeçalho → Pix pendente → vale ativo → fichas → comprar mais →
// ingressos → guardar link → como funciona → aviso do vale.
export default function CarteiraPage() {
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const { user: usuarioLogado } = useAuth();
  const logado = !!usuarioLogado;

  const [estado, setEstado] = useState<Estado>('carregando');
  const [dados, setDados] = useState<CarteiraPublica | null>(null);
  const [slugEvento, setSlugEvento] = useState<string | null>(null);
  const [visuais, setVisuais] = useState<Record<string, ProdutoVisual>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [retirada, setRetirada] = useState<{ ponto: PontoCarteira; foco: number | null } | null>(
    null,
  );
  const [cancelandoVale, setCancelandoVale] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [comprandoMais, setComprandoMais] = useState(false);
  const [vinculando, setVinculando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [outraGuardada, setOutraGuardada] = useState(false);
  // Permissão de aviso deste aparelho (só pedimos depois do toque).
  const [permissao, setPermissao] = useState(() => estadoPermissao());
  const [pedindoAviso, setPedindoAviso] = useState(false);
  // "Retirado!" — quando a barraca dá baixa no vale (o tempo real já relê).
  const [retirado, setRetirado] = useState(false);
  // Só dispara quando o total de fichas RETIRADAS sobe de verdade. Vale que
  // expira ou é cancelado não muda essa conta — então nunca dá falso positivo.
  const retiradasAnteriores = useRef<number | null>(null);
  // Convite para colocar na tela do celular: é o único lugar do app onde ele
  // aparece como cartão. "Agora não" vale 7 dias (mesma memória da faixa).
  const [mostrarInstalar, setMostrarInstalar] = useState(() => !faixaFechada());

  const carregandoRef = useRef(false);

  const carregar = useCallback(async () => {
    if (carregandoRef.current) return;
    carregandoRef.current = true;
    try {
      const { data, error } = await supabase.rpc('loja_carteira_publica', { p_carteira: codigo });
      if (error) throw error;
      const c = (Array.isArray(data) ? data[0] : data) as CarteiraPublica | null;
      if (!c?.carteira) {
        setEstado('nao_encontrado');
        return;
      }
      setDados(c);
      setEstado('pronto');
      // Só guarda sozinho se este celular ainda não tem carteira deste evento (ou se já é esta).
      if (c.carteira.evento_id) {
        const chave = chaveCarteiraLocal(c.carteira.evento_id);
        try {
          const atual = localStorage.getItem(chave);
          if (!atual || atual === c.carteira.codigo) localStorage.setItem(chave, c.carteira.codigo);
          else setOutraGuardada(true);
        } catch {
          /* armazenamento indisponível: segue */
        }
      }
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível carregar sua carteira.'));
      setEstado('erro');
    } finally {
      carregandoRef.current = false;
    }
  }, [codigo]);

  useEffect(() => {
    setEstado('carregando');
    carregar();
  }, [carregar, reload]);

  // Link da vitrine de fichas (para o "Comprar mais fichas") e o desenho de
  // cada produto (a carteira só devolve o nome).
  useEffect(() => {
    const eventoId = dados?.carteira.evento_id;
    if (!eventoId) return;
    let ativo = true;
    (async () => {
      try {
        const { data } = await supabase
          .from('eventos')
          .select('slug')
          .eq('id', eventoId)
          .maybeSingle();
        if (ativo) setSlugEvento((data as { slug: string } | null)?.slug ?? null);
      } catch {
        /* segue: o botão cai na vitrine geral */
      }

      try {
        const { data } = await supabase.rpc('loja_vitrine', { p_evento: eventoId });
        if (!ativo) return;
        // O id do ponto vem na barraca (o produto só traz nome e desenho).
        const vitrine = data as {
          pontos?: {
            ponto_id: number;
            produtos?: {
              nome: string;
              imagem_url: string | null;
              imagem_slug: string | null;
              emoji: string | null;
            }[];
          }[];
        } | null;

        const mapa: Record<string, ProdutoVisual> = {};
        (vitrine?.pontos ?? []).forEach((ponto) => {
          (ponto.produtos ?? []).forEach((produto) => {
            mapa[`${ponto.ponto_id}|${produto.nome}`] = {
              imagem_url: produto.imagem_url ?? null,
              imagem_slug: produto.imagem_slug ?? null,
              emoji: produto.emoji ?? null,
            };
          });
        });

        setVisuais(mapa);
      } catch {
        /* sem catálogo: fica o desenho neutro */
      }
    })();
    return () => {
      ativo = false;
    };
  }, [dados?.carteira.evento_id]);

  // Releitura a cada 5s enquanto a aba estiver visível + ao voltar o foco.
  useEffect(() => {
    if (estado !== 'pronto') return;
    const intervalo = window.setInterval(() => {
      if (document.visibilityState === 'visible') carregar();
    }, INTERVALO_POLLING_MS);
    const aoVisivel = () => {
      if (document.visibilityState === 'visible') carregar();
    };
    document.addEventListener('visibilitychange', aoVisivel);
    return () => {
      window.clearInterval(intervalo);
      document.removeEventListener('visibilitychange', aoVisivel);
    };
  }, [estado, carregar]);

  // Fichas retiradas: quando o número sobe, a barraca entregou — mostra o ✓.
  useEffect(() => {
    if (estado !== 'pronto' || !dados) return;
    const totalRetirado = dados.itens.reduce((soma, i) => soma + i.retirada, 0);
    const antes = retiradasAnteriores.current;
    retiradasAnteriores.current = totalRetirado;
    if (antes === null || totalRetirado <= antes) return;
    setRetirado(true);
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate?.(50);
    const t = window.setTimeout(() => setRetirado(false), 1600);
    return () => window.clearTimeout(t);
  }, [dados, estado]);

  // O cartão "Coloque na tela do celular" ocupa a vez: a faixa do topo fica calada.
  const temFichasAgora = (dados?.itens.length ?? 0) > 0;
  const temIngressosAgora = (dados?.ingressos.length ?? 0) > 0;
  const cartaoDeInstalarVisivel = mostrarInstalar && (temFichasAgora || temIngressosAgora);

  useEffect(() => {
    marcarConviteNaTela(cartaoDeInstalarVisivel);
    return () => marcarConviteNaTela(false);
  }, [cartaoDeInstalarVisivel]);

  async function copiarLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      /* não bloqueia */
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  function usarEstaCarteira() {
    if (!dados) return;
    try {
      localStorage.setItem(chaveCarteiraLocal(dados.carteira.evento_id), dados.carteira.codigo);
    } catch {
      /* ignora */
    }
    setOutraGuardada(false);
    mostrarToast('Pronto: as próximas compras deste celular vão para esta carteira.', 4000, 'sucesso');
  }

  function mandarParaMim() {
    if (!dados) return;
    const url = window.location.href;
    const texto = `Minha carteira do ${dados.carteira.evento_nome} — abra aqui:`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`${texto}\n${url}`)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  async function cancelarVale(valeCodigo: string) {
    if (!dados) return;
    setCancelandoVale(valeCodigo);
    try {
      const { error } = await supabase.rpc('loja_cancelar_vale', {
        p_carteira: dados.carteira.codigo,
        p_vale: valeCodigo,
      });
      if (error) throw error;
      setReload((r) => r + 1);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos cancelar o vale.'));
    } finally {
      setCancelandoVale(null);
    }
  }

  async function vincularConta() {
    if (!dados) return;
    setVinculando(true);
    try {
      const { error } = await supabase.rpc('carteira_vincular', {
        p_carteira: dados.carteira.codigo,
      });
      if (error) throw error;
      setReload((r) => r + 1);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos guardar a carteira.'));
    } finally {
      setVinculando(false);
    }
  }

  async function comprarMais() {
    if (!dados) return;
    setComprandoMais(true);
    try {
      if (slugEvento) navigate(`/e/${slugEvento}/fichas`);
      else navigate('/');
    } finally {
      setComprandoMais(false);
    }
  }

  function fecharInstalar() {
    setMostrarInstalar(false);
    fecharFaixa();
  }

  // Pede a permissão de aviso direto no aparelho (sem instrução de navegador).
  async function ligarAvisoDoVale() {
    setPedindoAviso(true);
    try {
      await ligarAvisosGerais();
      setPermissao(estadoPermissao());
      mostrarToast('Pronto: avisamos quando o vale for usado.', 5000, 'sucesso');
    } catch (e) {
      setPermissao(estadoPermissao());
      mostrarToast(mensagemAmigavel(e, 'Não conseguimos ligar os avisos.'), 5000, 'erro');
    } finally {
      setPedindoAviso(false);
    }
  }

  function aposGerarVale() {
    setReload((r) => r + 1);
  }

  // ————— Casca escura (mesma da vitrine de fichas) —————
  const casca = (conteudo: ReactNode) => (
    <div className="min-h-screen" style={{ background: NAVY }}>
      <div className="relative min-h-screen overflow-x-clip">{conteudo}</div>
    </div>
  );

  if (estado === 'carregando') {
    return casca(
      <div className="mx-auto w-full max-w-[720px] px-4 py-6">
        <div className="h-10 w-10 animate-pulse rounded-full bg-white/[0.07]" />
        <div className="mt-4 h-6 w-56 animate-pulse rounded-full bg-white/[0.07]" />
        <div className="mt-2 h-3 w-64 animate-pulse rounded-full bg-white/[0.05]" />
        <div className="mt-6 h-72 w-full animate-pulse rounded-[16px] bg-white/[0.06]" />
        <div className="mt-3 h-[76px] w-full animate-pulse rounded-[16px] bg-white/[0.05]" />
        <div className="mt-2 h-[76px] w-full animate-pulse rounded-[16px] bg-white/[0.05]" />
      </div>,
    );
  }

  if (estado === 'nao_encontrado' || estado === 'erro') {
    return casca(
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div className={`${CARTAO_ESCURO} w-full max-w-[420px] p-6 text-center`} style={ESTILO_CARTAO}>
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-white/50">
            <i
              className={
                estado === 'erro'
                  ? 'ri-error-warning-line text-[36px]'
                  : 'ri-wallet-3-line text-[36px]'
              }
              aria-hidden
            />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-white">
            {estado === 'erro' ? 'Não foi possível carregar' : 'Carteira não encontrada'}
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-white/65">
            {estado === 'erro'
              ? erro
              : 'O link pode estar incorreto ou a carteira não está mais disponível.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="aperta mt-6 h-[52px] w-full cursor-pointer whitespace-nowrap rounded-[16px] text-[15px] font-bold"
            style={{ background: OURO, color: NAVY }}
          >
            Voltar à vitrine
          </button>
        </div>
      </div>,
    );
  }

  if (!dados) return null;

  const { carteira, pendentes, vales, ingressos } = dados;

  // Junta o desenho do catálogo a cada item da carteira.
  const itens: ItemCarteira[] = dados.itens.map((item) => {
    const visual = visuais[`${item.ponto_id}|${item.produto}`];
    return visual ? { ...item, ...visual } : item;
  });

  const primeiroNome = carteira.nome.trim().split(/\s+/)[0] || carteira.nome;
  const minutosVale = Math.max(1, Math.round(TEMPO_VALE_MS / 60000));

  // Agrupa as fichas por barraca.
  const grupos = new Map<number, GrupoItens>();
  itens.forEach((item) => {
    const existente = grupos.get(item.ponto_id);
    if (existente) {
      existente.itens.push(item);
    } else {
      grupos.set(item.ponto_id, {
        ponto: { id: item.ponto_id, nome: item.ponto_nome },
        itens: [item],
      });
    }
  });
  const listaGrupos = Array.from(grupos.values());

  const temFichas = itens.length > 0;
  const tudoRetirado =
    temFichas && vales.length === 0 && itens.every((i) => i.disponivel === 0 && i.em_vale === 0);
  const temIngressos = ingressos.length > 0;
  const fichasParaRetirar = itens.reduce((soma, i) => soma + i.disponivel, 0);

  // Link de aviso: só quando o aparelho ainda não permitiu e pode pedir.
  const podeReceberAviso = suportaAvisos() && !precisaInstalarNoIphone();
  const mostrarLinkAviso = podeReceberAviso && permissao === 'default';

  return casca(
    <>
      <div className="app-entra">
        {/* Cabeçalho: voltar · evento · o que fazer com o QR */}
        <header className="relative z-20">
          <div className="mx-auto flex w-full max-w-[720px] items-start gap-3 px-4 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Voltar"
              className="aperta flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/15"
            >
              <i className="ri-arrow-left-line text-[20px]" aria-hidden />
            </button>
            <div className="min-w-0 flex-1">
              <p
                className="text-[11px] font-bold uppercase tracking-[0.08em]"
                style={{ color: OURO }}
              >
                Carteira de {primeiroNome}
              </p>
              <h1 className="mt-1 truncate text-[21px] font-extrabold leading-tight text-white">
                {carteira.evento_nome}
              </h1>
              <p className="mt-1 text-[13px] leading-snug text-white/70">
                Mostre o QR na barraca para retirar
              </p>
            </div>
          </div>
        </header>

        <div className="mx-auto w-full max-w-[720px] px-4 pb-16 pt-5">
          {/* 1. Colocar na tela do celular (único convite desta tela) */}
          {cartaoDeInstalarVisivel && (
            <div className={`${CARTAO_ESCURO} mb-4 p-4`} style={ESTILO_CARTAO}>
              <div className="flex items-start gap-3">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/[0.08]"
                  style={{ color: OURO }}
                >
                  <i className="ri-smartphone-line text-[19px]" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-extrabold text-white">
                    Coloque na tela do celular
                  </p>
                  <p className="mt-0.5 text-[12.5px] leading-snug text-white/55">
                    Suas fichas abrem sem internet, direto da tela inicial.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={fecharInstalar}
                  aria-label="Agora não"
                  className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-white/45 transition-colors hover:bg-white/10"
                >
                  <i className="ri-close-line text-[16px]" aria-hidden />
                </button>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    fecharInstalar();
                    navigate('/instalar');
                  }}
                  className="aperta h-11 flex-1 cursor-pointer whitespace-nowrap rounded-[12px] text-[13.5px] font-bold"
                  style={{ background: OURO, color: NAVY }}
                >
                  Instalar
                </button>
                <button
                  type="button"
                  onClick={fecharInstalar}
                  className="aperta h-11 flex-1 cursor-pointer whitespace-nowrap rounded-[12px] border border-white/15 text-[13.5px] font-semibold text-white/80 transition-colors hover:bg-white/[0.06]"
                >
                  Agora não
                </button>
              </div>
            </div>
          )}

          {/* 2. Pedido aguardando Pix */}
          {pendentes.length > 0 && (
            <div className="space-y-2">
              {pendentes.map((p) => (
                <button
                  key={p.pedido_codigo}
                  type="button"
                  onClick={() =>
                    navigate(
                      p.tipo === 'ingresso'
                        ? `/pedido/c/${p.pedido_codigo}`
                        : `/loja/pedido/${p.pedido_codigo}`,
                    )
                  }
                  className="aperta flex w-full cursor-pointer items-center justify-between gap-3 rounded-[16px] border border-warning-400/40 bg-warning-500/10 px-4 py-3 text-left transition-colors hover:bg-warning-500/15"
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warning-500/20 text-warning-300">
                      <i className="ri-time-line" aria-hidden />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-[14px] font-extrabold text-white">
                        Pedido nº {p.numero} aguardando Pix
                      </span>
                      <span className="block text-[12px] text-white/60">
                        <span style={{ color: OURO }}>{formatarCentavos(p.total_centavos)}</span> ·
                        toque para pagar
                      </span>
                    </span>
                  </span>
                  <span
                    className="shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] font-bold"
                    style={{ background: OURO, color: NAVY }}
                  >
                    Pagar agora
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* 3. Vale ativo */}
          {vales.length > 0 && (
            <div className={`${pendentes.length > 0 ? 'mt-4' : ''} space-y-3`}>
              {vales.map((vale) => (
                <CartaoVale
                  key={vale.codigo}
                  vale={vale}
                  cancelando={cancelandoVale === vale.codigo}
                  aoCancelar={cancelarVale}
                />
              ))}
            </div>
          )}

          {/* 4. Suas fichas */}
          {temFichas ? (
            <section className={vales.length > 0 || pendentes.length > 0 ? 'mt-6' : 'mt-1'}>
              <div className="flex items-end justify-between gap-3">
                <h2 className="text-[17px] font-extrabold text-white">Suas fichas</h2>
                <span className="whitespace-nowrap text-[12.5px] font-semibold text-white/55">
                  {fichasParaRetirar > 0 ? (
                    <>
                      <span style={{ color: OURO }}>{fichasParaRetirar}</span> para retirar
                    </>
                  ) : tudoRetirado ? (
                    'tudo retirado'
                  ) : (
                    'no vale'
                  )}
                </span>
              </div>

              <div className="mt-3 space-y-5">
                {listaGrupos.map((grupo) => (
                  <div key={grupo.ponto.id}>
                    <p className="mb-2 flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-[0.06em] text-white/50">
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-store-2-line" aria-hidden />
                      </span>
                      {grupo.ponto.nome}
                    </p>
                    <div className="space-y-2">
                      {grupo.itens.map((item) => (
                        <ItemFicha
                          key={item.item_id}
                          item={item}
                          aoRetirar={() => setRetirada({ ponto: grupo.ponto, foco: item.item_id })}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {tudoRetirado && (
                <p className="mt-3 rounded-[14px] border border-white/10 bg-white/[0.04] px-4 py-3 text-center text-[13px] font-semibold text-white/75">
                  Você já retirou tudo. Obrigado por apoiar o {carteira.evento_nome}!
                </p>
              )}
            </section>
          ) : (
            <section className={`${CARTAO_ESCURO} mt-1 p-6 text-center`} style={ESTILO_CARTAO}>
              <span className="mx-auto flex h-12 w-12 items-center justify-center text-white/40">
                <i className="ri-coupon-3-line text-[36px]" aria-hidden />
              </span>
              <p className="mt-3 text-[15px] font-bold text-white">
                Você ainda não tem fichas neste evento
              </p>
              <p className="mx-auto mt-1 max-w-[320px] text-[13px] leading-relaxed text-white/60">
                Compre fichas e elas ficam guardadas aqui, prontas para retirar nas barracas.
              </p>
            </section>
          )}

          {/* 5. Comprar mais fichas (contorno dourado) */}
          <button
            type="button"
            onClick={comprarMais}
            disabled={comprandoMais}
            className="aperta mt-4 flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[16px] border text-[15px] font-bold transition-colors hover:bg-white/[0.06] disabled:opacity-60"
            style={{ borderColor: 'rgba(232,200,119,0.55)', color: OURO, background: 'rgba(255,255,255,0.03)' }}
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-add-circle-line" aria-hidden />
            </span>
            {comprandoMais ? 'Abrindo…' : 'Comprar mais fichas'}
          </button>

          {/* 6. Seus ingressos (só quando existem) */}
          {temIngressos && (
            <section className="mt-8">
              <h2 className="text-[17px] font-extrabold text-white">Seus ingressos</h2>
              <p className="mt-0.5 text-[13px] text-white/60">
                {ingressos.length === 1 ? '1 ingresso' : `${ingressos.length} ingressos`} nesta
                carteira
              </p>
              <div className="mt-3 space-y-3">
                {ingressos.map((ing) => (
                  <CartaoIngresso
                    key={ing.codigo}
                    ingresso={ing}
                    eventoNome={carteira.evento_nome}
                    eventoInicio={carteira.evento_inicio}
                    eventoLocal={carteira.evento_local}
                    aoPagarPendente={(pedidoCodigo) => navigate(`/pedido/c/${pedidoCodigo}`)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* 7. Guardar esta carteira */}
          <GuardarCarteira
            copiado={copiado}
            aoCopiar={copiarLink}
            aoWhatsApp={mandarParaMim}
            vinculada={carteira.vinculada !== false}
            logado={logado}
            vinculando={vinculando}
            aoVincular={vincularConta}
            aoEntrar={() => navigate(`/entrar?voltar=${encodeURIComponent(`/carteira/${codigo}`)}`)}
          />

          {outraGuardada && (
            <div className="mt-3 rounded-[16px] border border-warning-400/40 bg-warning-500/10 p-4">
              <p className="text-[13px] leading-relaxed text-white/80">
                Este celular já guarda a carteira de outra pessoa. Quer usar esta aqui?
              </p>
              <button
                type="button"
                onClick={usarEstaCarteira}
                className="aperta mt-2 inline-flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[12px] border border-white/15 px-4 text-[13px] font-semibold text-white transition-colors hover:bg-white/[0.06]"
              >
                Usar esta carteira neste celular
              </button>
            </div>
          )}

          {/* 8. Como funciona? */}
          <ComoFunciona minutos={minutosVale} />

          {/* 9. Aviso do vale: uma linha, só se o aparelho ainda não permitiu */}
          {mostrarLinkAviso && (
            <div className="mt-4 flex items-center justify-center border-t border-white/10 pt-4">
              <button
                type="button"
                onClick={ligarAvisoDoVale}
                disabled={pedindoAviso}
                className="inline-flex min-h-[48px] cursor-pointer items-center gap-1.5 whitespace-nowrap px-2 text-[12.5px] font-semibold text-white/70 underline decoration-white/25 underline-offset-4 transition-colors hover:text-white disabled:opacity-60"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-notification-3-line" aria-hidden />
                </span>
                {pedindoAviso ? 'Ligando…' : 'Receber aviso quando o vale for usado'}
              </button>
            </div>
          )}

          {erro && (
            <p className="mt-4 rounded-[14px] border border-danger-400/40 bg-danger-500/10 px-4 py-3 text-[14px] text-danger-200">
              {erro}
            </p>
          )}
        </div>
      </div>

      {retirada && (
        <ModalRetirada
          carteiraCodigo={carteira.codigo}
          ponto={retirada.ponto}
          itens={itens.filter((i) => i.ponto_id === retirada.ponto.id && i.disponivel > 0)}
          itemFoco={retirada.foco}
          aoFechar={() => setRetirada(null)}
          aoGerado={aposGerarVale}
        />
      )}

      {/* A barraca deu baixa no vale: o ✓ confirma na hora, sem travar nada. */}
      <AnimatePresence>
        {retirado && (
          <motion.div
            className="fixed inset-0 z-[60] flex cursor-pointer flex-col items-center justify-center px-6 text-center"
            style={{ background: 'rgba(16,32,58,0.96)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            onClick={() => setRetirado(false)}
          >
            <CheckAnimado tamanho={96} cor={OURO} fundo="rgba(232,200,119,.14)" />
            <p className="mt-4 text-[24px] font-extrabold text-white">Retirado!</p>
            <p className="mt-1 text-[14px] text-white/65">
              Bom proveito — e obrigado por apoiar o {carteira.evento_nome}!
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
  );
}